/**
 * Согласие на необязательные категории данных (igra.now).
 *
 * Аналитика, чат поддержки и мониторинг ошибок не запускаются, пока человек
 * не согласился: с 2025–2026 годов согласие обязано предшествовать загрузке
 * скрипта, а не оформляться постфактум. Категории здесь — те же две, что в
 * документе «Передача данных и файлы cookie» (`/law/services`); третья
 * категория — обязательная — согласия не требует и потому в состоянии не
 * хранится.
 *
 * Хранилище — localStorage: выбор относится к браузеру, а не к аккаунту, и
 * должен переживать выход из кабинета. Любое обращение обёрнуто в try/catch:
 * приватное окно и «блокировать данные сайтов» бросают на самом обращении.
 */
export type ConsentCategory = 'analytics' | 'support'

export interface ConsentState {
  analytics: boolean
  support: boolean
  /** ISO-дата выбора — доказательство того, что согласие получено и когда. */
  date: string
}

const STORAGE_KEY = 'igra_consent_v1'

/** Событие для тех, кто грузится лениво: Sentry, счётчик, виджет поддержки. */
export const CONSENT_EVENT = 'igra-consent-updated'

export function readConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as Partial<ConsentState>
    return {
      analytics: parsed.analytics === true,
      support: parsed.support === true,
      date: typeof parsed.date === 'string' ? parsed.date : '',
    }
  } catch {
    return null
  }
}

export function writeConsent(choice: { analytics: boolean; support: boolean }): ConsentState {
  const state: ConsentState = { ...choice, date: new Date().toISOString() }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Выбор не сохранился — баннер спросит снова. Это лучше, чем считать
    // согласие данным на слово.
  }
  window.dispatchEvent(new CustomEvent<ConsentState>(CONSENT_EVENT, { detail: state }))
  return state
}

/** Нет выбора — нет согласия: по умолчанию всё необязательное выключено. */
export function hasConsent(category: ConsentCategory): boolean {
  return readConsent()?.[category] === true
}

export function consentDecided(): boolean {
  return readConsent() !== null
}

/** Подписка на изменение выбора; возвращает функцию отписки. */
export function onConsentChange(handler: (state: ConsentState) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<ConsentState>).detail)
  window.addEventListener(CONSENT_EVENT, listener)
  return () => window.removeEventListener(CONSENT_EVENT, listener)
}
